/**
 * User Layout V2 Component
 * ========================
 * 
 * Main layout wrapper for user dashboard pages (V2 architecture).
 * Uses generic Sidebar component for consistency with AdminLayout.
 * 
 * Features:
 * - Sidebar navigation (Open RFQ, Closed RFQ, Create Request, Inbox, Settings)
 * - Top header with user info and logout
 * - Page title and description
 * - Header actions (customizable per page)
 * - Responsive design
 * 
 * Props:
 * - user: User object (name, email, company, etc.)
 * - currentRoute: Current active route
 * - onNavigate: Navigation handler
 * - onLogout: Logout handler
 * - pageTitle: Page title
 * - pageDescription: Page description
 * - headerActions: Optional header actions (buttons, filters, etc.)
 * - children: Page content
 */

import React, { useMemo, useEffect, useState } from 'react';
import Sidebar, { NavItem } from '../../../common/Sidebar';
import SecondarySidebar, { SecondaryMenuItem } from '../../../common/SecondarySidebar';
import { API_ENDPOINTS } from '../../../../utils/config';
import './UserLayout.scss';

interface UserLayoutProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  pageTitle: string;
  pageDescription?: string;
  headerActions?: React.ReactNode;
  navigationActions?: React.ReactNode; // Navigation buttons (Back/Next) to show below header
  children: React.ReactNode;
  cartItemCount?: number;
  cartButton?: {
    label: string;
    onClick: () => void;
    className?: string;
  };
  noSidebarMargin?: boolean; // Sidebar margin'ini kaldırmak için
  hideHeader?: boolean; // Header'ı gizlemek için
  noContentPadding?: boolean; // Content padding'ini kaldırmak için
  disableInnerScroll?: boolean; // İç scroll'u devre dışı bırakmak için
  secondarySidebar?: React.ReactNode | {
    title: string;
    menuItems: SecondaryMenuItem[];
    activeItemId?: string;
  };
  /** Dynamic sub-items function for sidebar navigation */
  getDynamicSubItems?: (parentId: string, currentRoute: string, permissions?: any, userRole?: string, userCompanyPlan?: string) => Array<{
    id: string;
    label: string;
    route: string;
    icon?: string;
  }> | undefined;
}

const UserLayout: React.FC<UserLayoutProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  pageTitle,
  pageDescription,
  headerActions,
  navigationActions,
  children,
  cartItemCount = 0,
  cartButton,
  noSidebarMargin = false,
  hideHeader = false,
  noContentPadding = false,
  disableInnerScroll = false,
  secondarySidebar,
  getDynamicSubItems
}) => {
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [hasMentions, setHasMentions] = useState(false);
  const [inboxOpenUnreadCount, setInboxOpenUnreadCount] = useState(0);
  const [inboxFinalizedUnreadCount, setInboxFinalizedUnreadCount] = useState(0);
  const [inboxOpenHasMentions, setInboxOpenHasMentions] = useState(false);
  const [inboxFinalizedHasMentions, setInboxFinalizedHasMentions] = useState(false);
  const [globalCartCount, setGlobalCartCount] = useState(0);

  // Get user's company ID for filtering - try from user object first, then from route
  const userCompanyId = user?.company_id || user?.company?._id || user?.company;

  // Extract companyId from route if we're on a company page
  const routeMatch = currentRoute.match(/\/dashboard\/companies\/([^/]+)/);
  const routeCompanyId = routeMatch ? routeMatch[1] : null;

  // Use route companyId if available, otherwise fall back to user companyId
  const companyId = routeCompanyId || userCompanyId;

  // Use passed cartItemCount if available, otherwise use globally fetched count
  const effectiveCartCount = cartItemCount > 0 ? cartItemCount : globalCartCount;

  // Fetch global cart count for sidebar badge
  useEffect(() => {
    const fetchCartCount = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token || !user?._id) return;

      try {
        const response = await fetch(API_ENDPOINTS.PREREQUESTS, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-User-ID': user._id
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setGlobalCartCount(data.data.items?.length || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching cart count for sidebar:', error);
      }
    };

    fetchCartCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchCartCount, 30000);

    // Listen for cart update events
    const handleCartUpdate = () => {
      fetchCartCount();
    };
    window.addEventListener('cart-updated', handleCartUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, [user?._id]);

  // Fetch inbox unread count with phase filtering
  useEffect(() => {
    const fetchUnreadCountByPhase = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      try {
        // Fetch inbox items
        const inboxResponse = await fetch(API_ENDPOINTS.USER_INBOX, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!inboxResponse.ok) {
          throw new Error(`HTTP error! status: ${inboxResponse.status}`);
        }

        const inboxData = await inboxResponse.json();
        if (!inboxData.success) {
          throw new Error(inboxData.error || 'Failed to load inbox items');
        }

        const items = inboxData.data || [];

        // NOTE: Backend already includes 'phase' in the list response via aggregation pipeline
        // No need to fetch REQUEST_V2_DETAIL for each item anymore!
        // This was causing N+1 queries (281+ requests per page load)

        // Calculate unread counts by phase
        // Use a Map to track unique request_id's to avoid counting duplicates
        const requestCounts = new Map<string, { unreadCount: number; isMentioned: boolean; phase: string }>();

        items.forEach((item: any) => {
          if (!item.phase || !item.request_id) return; // Skip items without phase or request_id

          const phase = item.phase.toLowerCase();
          const unreadCount = item.unread_count || 0;
          const isMentioned = item.is_mentioned || false;
          const requestId = String(item.request_id);

          // Track by request_id to avoid duplicates
          if (!requestCounts.has(requestId)) {
            requestCounts.set(requestId, {
              unreadCount: unreadCount,
              isMentioned: isMentioned,
              phase: phase
            });
          } else {
            // If duplicate, use the maximum unread_count and combine mentions
            const existing = requestCounts.get(requestId)!;
            requestCounts.set(requestId, {
              unreadCount: Math.max(existing.unreadCount, unreadCount),
              isMentioned: existing.isMentioned || isMentioned,
              phase: phase
            });
          }
        });

        let openUnreadCount = 0;
        let finalizedUnreadCount = 0;
        let openHasMentions = false;
        let finalizedHasMentions = false;

        // Calculate counts from unique requests
        requestCounts.forEach((data, requestId) => {
          const phase = data.phase;
          const unreadCount = data.unreadCount;
          const isMentioned = data.isMentioned;

          // Open: exclude deleted, rejected, select_supplier, cancelled
          if (phase !== 'deleted' &&
            phase !== 'rejected' &&
            phase !== 'select_supplier' &&
            phase !== 'cancelled') {
            openUnreadCount += unreadCount;
            if (isMentioned) openHasMentions = true;
          }

          // Finalized: only select_supplier, cancelled, rejected (exclude deleted)
          if (phase !== 'deleted' &&
            (phase === 'select_supplier' || phase === 'cancelled' || phase === 'rejected')) {
            finalizedUnreadCount += unreadCount;
            if (isMentioned) finalizedHasMentions = true;
          }
        });

        console.log('📬 Inbox unread counts - Open:', openUnreadCount, 'Finalized:', finalizedUnreadCount);
        setInboxOpenUnreadCount(openUnreadCount);
        setInboxFinalizedUnreadCount(finalizedUnreadCount);
        setInboxOpenHasMentions(openHasMentions);
        setInboxFinalizedHasMentions(finalizedHasMentions);

        // Also set total for backward compatibility
        setInboxUnreadCount(openUnreadCount + finalizedUnreadCount);
        setHasMentions(openHasMentions || finalizedHasMentions);
      } catch (error) {
        console.error('❌ Error fetching inbox unread count by phase:', error);
      }
    };

    fetchUnreadCountByPhase();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCountByPhase, 30000);

    // Listen for inbox update events
    const handleInboxUpdate = () => {
      fetchUnreadCountByPhase();
    };

    window.addEventListener('inbox-updated', handleInboxUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('inbox-updated', handleInboxUpdate);
    };
  }, []);

  // Get user permissions - handle both object and array formats
  const permissions = user?.permissions || {};
  const permissionsObj = Array.isArray(permissions)
    ? {}
    : (permissions as { [key: string]: boolean });
  const hasOpenRfqRequest = permissionsObj?.open_rfq_request === true;
  const hasEditSupplier = permissionsObj?.edit_supplier === true;
  const hasEditProduct = permissionsObj?.edit_product === true;
  const hasUserRights = permissionsObj?.user_rights === true;
  const hasEditAddress = permissionsObj?.edit_address === true;
  const hasApproveAbility = permissionsObj?.approve_ability === true;

  // Debug: Log permissions for troubleshooting
  useEffect(() => {
    console.log('🔍 UserLayout - User permissions:', permissions);
    console.log('🔍 UserLayout - hasOpenRfqRequest:', hasOpenRfqRequest);
    console.log('🔍 UserLayout - hasEditSupplier:', hasEditSupplier);
    console.log('🔍 UserLayout - hasEditProduct:', hasEditProduct);
    console.log('🔍 UserLayout - hasUserRights:', hasUserRights);
    console.log('🔍 UserLayout - hasEditAddress:', hasEditAddress);
    console.log('🔍 UserLayout - hasApproveAbility:', hasApproveAbility);
    console.log('🔍 UserLayout - companyId:', companyId);
  }, [permissions, hasOpenRfqRequest, hasEditSupplier, hasEditProduct, hasUserRights, hasEditAddress, hasApproveAbility, companyId]);

  // Navigation items for user dashboard - memoized to prevent recreation on every render
  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];

    // 1. Open RFQs
    if (hasOpenRfqRequest) {
      const openRfqItem: NavItem = {
        id: 'inbox-open',
        label: 'Open RFQs',
        icon: '●',
        route: '/dashboard/user/inbox/open',
        description: 'Active requests in progress',
        badge: inboxOpenUnreadCount > 0 ? inboxOpenUnreadCount : undefined,
        badgeVariant: inboxOpenHasMentions ? 'mentioned' : 'default'
      };

      // Add dynamic sub-items if function is provided and we're on RFQ detail page
      if (getDynamicSubItems) {
        // Pass permissions and company plan to getDynamicSubItems
        const dynamicSubItems = getDynamicSubItems(
          'inbox-open',
          currentRoute,
          permissionsObj,
          user?.role,
          user?.company?.plan
        );
        console.log('🔍 UserLayout - getDynamicSubItems called:', {
          parentId: 'inbox-open',
          currentRoute,
          permissions: permissionsObj,
          dynamicSubItems,
          hasItems: dynamicSubItems && dynamicSubItems.length > 0
        });
        if (dynamicSubItems && dynamicSubItems.length > 0) {
          openRfqItem.children = dynamicSubItems.map(subItem => ({
            id: subItem.id,
            label: subItem.label,
            icon: subItem.icon || '●',
            route: subItem.route
          }));
          // Route preserved to allow navigation + dropdown
          console.log('🔍 UserLayout - Open RFQs item with children:', openRfqItem);
        }
      }

      items.push(openRfqItem);
    }

    // 2. Finalized RFQs
    if (hasOpenRfqRequest) {
      items.push({
        id: 'inbox-finalized',
        label: 'Finalized RFQs',
        icon: '●',
        route: '/dashboard/user/inbox/finalized',
        description: 'Completed and cancelled requests',
        badge: inboxFinalizedUnreadCount > 0 ? inboxFinalizedUnreadCount : undefined,
        badgeVariant: inboxFinalizedHasMentions ? 'mentioned' : 'default'
      });
    }

    // 3. Marketplace (Create Request)
    if (hasOpenRfqRequest) {
      items.push({
        id: 'create-request',
        label: 'Marketplace',
        icon: '●',
        route: '/dashboard/user/create-request/marketplace',
        description: 'Browse products and create requests'
      });
    }

    // 4. Shopping Cart
    if (hasOpenRfqRequest) {
      items.push({
        id: 'cart',
        label: 'Shopping Cart',
        icon: '●',
        route: '/dashboard/user/cart',
        description: 'View and manage your cart',
        badge: effectiveCartCount
      });
    }



    // 5. Separator
    if (companyId && (hasEditSupplier || hasEditProduct || hasUserRights)) {
      items.push({
        id: 'separator-1',
        label: '',
        icon: '',
        description: ''
      });
    }

    // 6. Supplier List
    if (companyId && hasEditSupplier) {
      items.push({
        id: 'company-suppliers',
        label: 'Supplier List',
        icon: '●',
        route: `/dashboard/companies/${companyId}/suppliers`,
        description: 'View company suppliers'
      });
    }

    // 7. Product List
    if (companyId && hasEditProduct) {
      items.push({
        id: 'company-products',
        label: 'Product List',
        icon: '●',
        route: `/dashboard/companies/${companyId}/products`,
        description: 'View company products'
      });
    }

    // 8. Customer List (User List)
    if (companyId && hasUserRights) {
      items.push({
        id: 'company-users',
        label: 'User List',
        icon: '●',
        route: `/dashboard/companies/${companyId}/users`,
        description: 'View and manage company users'
      });
    }

    // 9. Separator
    items.push({
      id: 'separator-2',
      label: '',
      icon: '',
      description: ''
    });

    // 10. System Settings
    items.push({
      id: 'settings',
      label: 'System Settings',
      icon: '●',
      route: '/dashboard/user/settings',
      description: 'Account and preferences'
    });



    // User Right Definition - not in the requested order, keeping it at the end if needed
    if (companyId && hasUserRights) {
      items.push({
        id: 'company-user-rights',
        label: 'User Right Definition',
        icon: '●',
        route: `/dashboard/companies/${companyId}/user-rights`,
        description: 'Define user access rights'
      });
    }

    console.log('🔍 UserLayout - navItems created:', items.map(item => item.id));
    return items;
  }, [companyId, cartItemCount, effectiveCartCount, globalCartCount, hasOpenRfqRequest, hasEditSupplier, hasEditProduct, hasUserRights, hasEditAddress, hasApproveAbility, user?.permissions, user?.company_id, user?.company, inboxUnreadCount, hasMentions, inboxOpenUnreadCount, inboxFinalizedUnreadCount, inboxOpenHasMentions, inboxFinalizedHasMentions, getDynamicSubItems, currentRoute]);

  return (
    <div className={`user-layout-v2 ${secondarySidebar ? 'with-secondary-sidebar' : ''}`}>
      {/* Generic Sidebar */}
      <Sidebar
        logo={user?.company?.name || 'Gordian'}
        logoUrl={user?.company?.logo_url}
        navItems={navItems}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        user={{
          name: user?.name || 'User',
          email: user?.email || '',
          avatar: undefined
        }}
        onLogout={onLogout}
        variant="user"
        cartButton={cartButton}
      />

      {/* Secondary Sidebar (if provided) */}
      {secondarySidebar && (
        React.isValidElement(secondarySidebar) ? (
          secondarySidebar
        ) : typeof secondarySidebar === 'object' && 'title' in secondarySidebar && 'menuItems' in secondarySidebar ? (
          <SecondarySidebar
            title={secondarySidebar.title}
            menuItems={secondarySidebar.menuItems}
            activeItemId={secondarySidebar.activeItemId}
          />
        ) : null
      )}

      {/* Main Content */}
      <main
        className={`user-layout-v2__main ${secondarySidebar ? 'user-layout-v2__main--with-secondary' : ''}`}
        style={noSidebarMargin ? { marginLeft: 0 } : undefined}
      >
        {/* Header */}
        {!hideHeader && (
          <header className="user-layout-v2__header">
            <div className="user-layout-v2__header-left">
              <h1 className="user-layout-v2__page-title">{pageTitle}</h1>
              {pageDescription && (
                <p className="user-layout-v2__page-description">{pageDescription}</p>
              )}
            </div>
            {headerActions && (
              <div className="user-layout-v2__header-actions">
                {headerActions}
              </div>
            )}
          </header>
        )}

        {/* Navigation Actions (Back/Next buttons) */}
        {navigationActions && (
          <div className="user-layout-v2__navigation-actions">
            {navigationActions}
          </div>
        )}

        {/* Page Content */}
        <div className={`user-layout-v2__content ${noContentPadding ? 'user-layout-v2__content--no-padding' : ''} ${disableInnerScroll ? 'user-layout-v2__content--scroll-visible' : ''}`}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default UserLayout;

